import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useIntl } from 'react-intl';
import JoditEditor from 'jodit-react';
import sanitizePastedHtml from 'utils/sanitizePastedHtml';

interface TextEditorProps {
    disabled?: boolean;
    placeholder?: string;
    setHtmlOutput?: React.Dispatch<React.SetStateAction<string>>;
    initial: string;
    width?: string;
    toolbar?: boolean;
    footer?: boolean;
    chartLimit?: number;
    onEditorReady?: (editor: any) => void;
}

const TextEditor: React.FC<TextEditorProps> = ({
    disabled = false,
    placeholder = '',
    setHtmlOutput,
    initial,
    width = 'auto',
    toolbar = true,
    footer = true,
    chartLimit = 2000,
    onEditorReady,
}) => {
    const [mountValue] = useState(initial || '');
    const editorRef = useRef<any>(null);
    // Last value the editor and the parent agree on. Guards against writing back
    // the value the editor itself just emitted, which would reset the caret.
    const syncedValueRef = useRef<string>(initial || '');
    // Read through a ref so a parent passing an inline callback does not change `config`:
    // jodit-react destroys and re-makes the editor on every config identity change, and
    // afterInit -> onEditorReady -> parent setState -> new callback would loop forever.
    const onEditorReadyRef = useRef(onEditorReady);
    onEditorReadyRef.current = onEditorReady;

    const intl = useIntl();

    // Apply every outside change of `initial` (e.g. switching the template language),
    // not just the first one.
    useEffect(() => {
        const nextValue = initial || '';

        if (nextValue === syncedValueRef.current) {
            return;
        }

        syncedValueRef.current = nextValue;

        if (editorRef.current) {
            editorRef.current.value = nextValue;
        }
    }, [initial]);

    const config = useMemo(() => ({
        buttons: [
            'bold',
            'italic',
            'underline',
            'strikethrough',
            'ul',
            'ol',
            'hr',
            'font',
            'brush',
            'lineHeight',
            'copyformat',
            'fontsize',
            'image',
            'video',
            'table',
            'link',
            'indent',
            'outdent',
            'left',
            'right',
            'center',
            'justify',
            'undo',
            'redo',
            'preview',
            'source',
        ],
        toolbar: toolbar,
        statusbar: footer,
        askBeforePasteHTML: false,
        popup: {
            closeOnClickOutside: false,
        },
        readonly: disabled,
        width: width,
        placeholder: placeholder || '',
        limitChars: chartLimit,
        hidePoweredByJodit: true,
        uploader: {
            insertImageAsBase64URI: true,
        },
        events: {
            afterInit: (editor: any) => {
                editorRef.current = editor;

                // Content can arrive before the editor finishes initializing.
                if (syncedValueRef.current && syncedValueRef.current !== editor.value) {
                    editor.value = syncedValueRef.current;
                }

                onEditorReadyRef.current?.(editor);
            },
            // Strips the clipboard leftovers of a paste from another application, which is
            // how invisible text used to reach the template.
            processPaste: (_event: unknown, pasted: unknown) =>
                typeof pasted === 'string' ? sanitizePastedHtml(pasted) : undefined,
        },
        controls: {
            link: {
                popup: (editor: any, current: Node | null, self: any, close: any) => {
                    const form = editor.create.fromHTML(
                        `
                        <form class="jodit-ui-form" dir="auto" novalidate="">

                            <div class="jodit-ui-block jodit-ui-block_align_left">
                                <div class="jodit-ui-input jodit-ui-block__url">
                                    <span class="jodit-ui-input__label">` +
                            intl.formatMessage({ id: 'general.url' }) +
                            `</span>
                                    <div class="jodit-ui-input__wrapper">
                                        <input class="jodit-ui-input__input" dir="auto" name="url" type="text" data-ref="url_input"
                                            ref="url_input" required="true" placeholder="http://">
                                    </div>
                                </div>
                            </div>

                            <div class="jodit-ui-block jodit-ui-block_align_left" data-ref="content_input_box" ref="content_input_box">
                                <div class="jodit-ui-input jodit-ui-block__content">
                                    <span class="jodit-ui-input__label">` +
                            intl.formatMessage({ id: 'general.text' }) +
                            `</span>
                                    <div class="jodit-ui-input__wrapper">
                                        <input class="jodit-ui-input__input" dir="auto" name="content" type="text" data-ref="content_input"
                                            ref="content_input" placeholder="">
                                    </div>
                                </div>
                            </div>
                            <label
                                class="jodit-ui-checkbox jodit-ui-checkbox_checked_false jodit-ui-checkbox_switch_false jodit-ui-form__target">
                                <span class="jodit-ui-checkbox__label">` +
                            intl.formatMessage({ id: 'general.open_new_tab' }) +
                            `</span>
                                <div class="jodit-ui-checkbox__wrapper">
                                    <input class="jodit-ui-checkbox__input" dir="auto" name="target" type="checkbox" data-ref="target_checkbox"
                                        ref="target_checkbox" placeholder="" value="false">
                                </div>
                            </label>

                            <label
                                class="jodit-ui-checkbox jodit-ui-checkbox_checked_false jodit-ui-checkbox_switch_false jodit-ui-form__target">
                                <span class="jodit-ui-checkbox__label">` +
                            intl.formatMessage({ id: 'general.is_email' }) +
                            `</span>
                                <div class="jodit-ui-checkbox__wrapper">
                                    <input class="jodit-ui-checkbox__input" dir="auto" name="target2" type="checkbox"
                                        data-ref="target2_checkbox" ref="target2_checkbox" placeholder="" value="false">
                                </div>
                            </label>

                            <div class="jodit-ui-block jodit-ui-block_align_full">
                                <button
                                    class="jodit-ui-button jodit-ui-button_size_middle jodit-ui-button_unlink jodit-ui-button_variant_default jodit-ui-button_text-icons_true jodit-ui-block__unlink"
                                    type="button" role="button" aria-pressed="false" data-ref="unlink" ref="unlink" style="display: none;">
                                    <span class="jodit-ui-button__icon"></span>
                                    <span class="jodit-ui-button__text">Unlink</span></button>
                                <button
                                    class="jodit-ui-button jodit-ui-button_size_middle jodit-ui-button_insert jodit-ui-button_variant_primary jodit-ui-button_text-icons_true jodit-ui-block__insert"
                                    type="submit" role="button" aria-pressed="false" data-ref="insert" ref="insert">
                                    <span class="jodit-ui-button__icon"></span>
                                    <span class="jodit-ui-button__text">` +
                            intl.formatMessage({ id: 'general.insert' }) +
                            `</span></button>
                            </div>
                        </form>
                    `
                    );

                    const selectedRange: Range = editor.s.range;
                    let selectedLink: HTMLAnchorElement | null = null;

                    if (current && current instanceof HTMLAnchorElement) {
                        selectedLink = current;
                    }

                    if (
                        current?.parentElement &&
                        current.parentElement instanceof HTMLAnchorElement
                    ) {
                        selectedLink = current.parentElement;
                    }

                    if (selectedLink) {
                        form.querySelector('input[name="url"]').value = selectedLink.href.replace(
                            /^mailto:/,
                            ''
                        );
                        form.querySelector('input[name="content"]').value =
                            selectedLink.textContent || '';
                        form.querySelector('input[name="target"]').checked =
                            selectedLink.target === '_blank';
                        form.querySelector('input[name="target2"]').checked =
                            selectedLink.href.startsWith('mailto:');
                    } else if (selectedRange) {
                        form.querySelector('input[name="content"]').value =
                            selectedRange.toString();
                    }

                    form.addEventListener('submit', (e: any) => {
                        e.preventDefault();

                        if (selectedLink) {
                            editor.s.select(selectedLink);
                            editor.s.removeNode(selectedLink);
                        } else if (selectedRange.toString()) {
                            editor.s.selectRange(selectedRange);
                            selectedRange.deleteContents();
                        }

                        const mailto = form.querySelector('input[name="target2"]').checked;
                        const url = form.querySelector('input[name="url"]').value;
                        const text = form.querySelector('input[name="content"]').value || url;
                        const target = form.querySelector('input[name="target"]').checked
                            ? '_blank'
                            : '_self';

                        const href = mailto ? `mailto:${url}` : url;

                        const a = editor.c.element('a', { href, target });
                        a.textContent = text;

                        editor.s.insertNode(a, true);

                        close();
                    });

                    form.addEventListener('mousedown', (e: any) => {
                        e.stopPropagation();
                    });

                    return form;
                },
            },
        },
    }), [disabled, toolbar, footer, width, placeholder, chartLimit, intl]);

    return (
        <JoditEditor
            config={config}
            onChange={(value: string) => {
                syncedValueRef.current = value ?? '';

                if (!disabled && setHtmlOutput) {
                    setHtmlOutput(value);
                }
            }}
            value={mountValue}
        />
    );
};

export default TextEditor;
